import { SimpleCard } from "@/components/DataCard";
import { BarChart } from "@/components/BarChart";

export const SendingTimePatternAnalysis = () => {
  return (
    <section className="section-card mt-5 sm:mt-7">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1.5 sm:gap-2 mb-5 sm:mb-6">
        <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-foreground">
          Spam rates by sending volume
        </h2>
        <p className="text-[11px] sm:text-xs text-muted-foreground">
          How email volume affects spam placement
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
        <SimpleCard title="Spam rate by monthly volume">
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">50k-200k emails/month</span>
                <span className="font-semibold text-destructive">29.31% spam</span>
              </div>
              <BarChart percentage={29} variant="red" />
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">10k-50k emails/month</span>
                <span className="font-semibold text-destructive">25.76% spam</span>
              </div>
              <BarChart percentage={26} variant="red" delay={100} />
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">1-10k emails/month</span>
                <span className="font-semibold text-warning">21.64% spam</span>
              </div>
              <BarChart percentage={22} variant="red" delay={200} />
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">1M+ emails/month</span>
                <span className="font-semibold text-primary">16.24% spam</span>
              </div>
              <BarChart percentage={16} delay={300} />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mt-3 pt-2 border-t border-border">
            High-volume senders with consistent patterns achieve lower spam rates
          </p>
        </SimpleCard>

        <SimpleCard title="Why unsubscribes happen">
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Emails not relevant</span>
                <span className="font-semibold text-destructive">40% unsubscribe</span>
              </div>
              <BarChart percentage={40} variant="red" />
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Content feels "too spammy"</span>
                <span className="font-semibold text-destructive">28% unsubscribe</span>
              </div>
              <BarChart percentage={28} variant="red" delay={100} />
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Emails too frequent</span>
                <span className="font-semibold text-destructive">27% unsubscribe</span>
              </div>
              <BarChart percentage={27} variant="red" delay={200} />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mt-3 pt-2 border-t border-border">
            Source: Industry email marketing benchmarks 2024
          </p>
        </SimpleCard>

        <SimpleCard title="How AI-driven spam filtering works" className="md:col-span-2">
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            According to Litmus (2025), mailbox providers now deploy <strong className="text-foreground">AI-based filtering systems</strong> that 
            evaluate emails in context rather than relying solely on keywords. Gmail and Microsoft use 
            <strong className="text-foreground"> large language models</strong> to identify unnatural tone, repeated patterns, 
            or mass-generated templates often linked to automation or spam.
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Security scanning tools like <strong className="text-foreground">Microsoft Defender</strong> automatically click every link 
            to check for phishing sites before emails reach recipients. This means clicks occurring within 
            <strong className="text-foreground"> 2-3 seconds of delivery</strong> are almost universally flagged as non-human interactions. 
            For marketers, this means traditional open and click metrics are contaminated with 30-40% bot activity.
          </p>
        </SimpleCard>
      </div>
    </section>
  );
};
